import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { Box, IconButton, Stack, Typography } from "@mui/material";
import { useUpdateBalanceVisibilityMutation } from "Api/Mutations/User/useUpdateBalanceVisibilityMutation";
import { useUserInfoQuery } from "Auth/Api/Queries/useUserInfoQuery";
import { useIsUserVerified } from "Hooks/useIsUserVerified";
import { Resources, useResource } from "Translations/Resources";
import { formatCurrency } from "Utils/CurrencyUtils";

const BALANCE_LABEL_RESOURCE = Resources.Common.Balance;
const VISIBILITY_RESOURCES = Resources.Balance.BalanceContent;

type Props = {
  balance: number | null;
  currency: string;
  isReceiveOnly: boolean;
  isDisabled?: boolean;
};

export const BankAccountBalance = ({
  balance,
  currency,
  isReceiveOnly,
  isDisabled,
}: Props) => {
  const { t } = useResource();
  const { data: userInfo } = useUserInfoQuery();
  const { isUserVerified } = useIsUserVerified();
  const { mutate: updateBalanceVisibility, isPending: isUpdatingVisibility } =
    useUpdateBalanceVisibilityMutation();

  if (isReceiveOnly || !isUserVerified) {
    return null;
  }

  const isBalanceVisible = userInfo?.isBalanceVisible ?? false;
  const formattedAmount =
    balance === null
      ? "—"
      : formatCurrency({
          amount: balance,
          currencyCode: currency,
          isNumberOnly: true,
          minDecimalPlaces: 2,
          maxDecimalPlaces: 2,
        });

  return (
    <Stack
      gap={0.5}
      sx={isDisabled ? { opacity: 0.5, pointerEvents: "none" } : undefined}
    >
      <Box display="flex" alignItems="center" gap={1}>
        <Typography
          fontSize="0.75rem"
          fontWeight={500}
          color="text.secondary"
          sx={{ textTransform: "uppercase", letterSpacing: "0.05em" }}
        >
          {t(BALANCE_LABEL_RESOURCE)}
        </Typography>
        <IconButton
          size="small"
          disabled={isUpdatingVisibility || !userInfo}
          onClick={() =>
            userInfo &&
            updateBalanceVisibility({ isVisible: !userInfo.isBalanceVisible })
          }
          aria-label={
            isBalanceVisible
              ? t(VISIBILITY_RESOURCES.HideBalance)
              : t(VISIBILITY_RESOURCES.ShowBalance)
          }
          sx={{
            p: 0.5,
            color: "text.secondary",
            "&.Mui-disabled": { color: "text.secondary" },
          }}
        >
          {isBalanceVisible ? (
            <VisibilityOutlinedIcon fontSize="small" />
          ) : (
            <VisibilityOffOutlinedIcon fontSize="small" />
          )}
        </IconButton>
      </Box>
      <Box
        display="flex"
        alignItems="baseline"
        gap={1}
        sx={{
          filter: isBalanceVisible ? "none" : "blur(8px)",
          pointerEvents: isBalanceVisible ? "auto" : "none",
          userSelect: isBalanceVisible ? "auto" : "none",
          transition: "filter 0.15s ease",
        }}
      >
        <Typography fontSize="2rem" fontWeight={700} lineHeight={1}>
          {formattedAmount}
        </Typography>
        {balance !== null && (
          <Typography fontSize="1rem" color="text.secondary">
            {currency}
          </Typography>
        )}
      </Box>
    </Stack>
  );
};
